// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount, pluralize } = require('shared/string-utils')
// Common client-V2 imports
const tag = require('client-v2/common/utils/hyperscript')
const CirclePositioner = require('client-v2/common/components/circle-positioner')
const { CollectionSubscribeCircleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
const {
  collectionProfileHref,
  collectionProfileSubscribersHref,
  collectionUserProfileHref
} = require('client-v2/common/models/collection/actions')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const PageContextRecord = require('client-v2/common/models/page-context/record')

const bem = BemEntity('CollectionCard')
const Subscribers = pluralize('Subscriber')

class CollectionCard extends React.PureComponent {
  constructor(props) {
    super(props)

    // Ensure function references do not change between render calls
    registerComponentEventHandlers(this)
  }

  // Linkage methods

  collectionHref() {
    if (this.props.isHrefsEnabled) {
      return collectionProfileHref(this.props.collection)
    }
  }

  collectionSubscribersHref() {
    if (this.props.isHrefsEnabled) {
      return collectionProfileSubscribersHref(this.props.collection)
    }
  }

  userHref() {
    if (this.props.isHrefsEnabled) {
      return collectionUserProfileHref(this.props.collection)
    }
  }

  // Render methods

  renderCaption() {
    const { visibility } = this.props.collection
    const isPrivate = visibility === CollectionModelRecord.VISIBILITY_PRIVATE
    return tag('div', {
      className: bem.element('caption', { private: isPrivate })
    },
      isPrivate && 'Private ',
      'Collection'
    )
  }

  renderCollectionName() {
    const { collection } = this.props
    return (
      tag('a', {
        className: bem.element('collection-name'),
        href: this.collectionHref(),
        onClick: this.onTitleClick
      },
        collection.name
      )
    )
  }

  renderDetails() {
    const isUserVisible = this.props.isUserVisible && !this.props.isSelf
    return (
      tag('div', {
        className: bem.element('details')
      },
        tag('div', {
          className: bem.element('details-top')
        },
          this.renderCaption(),
          this.renderCollectionName()
        ),
        isUserVisible && tag('div', {
          className: bem.element('details-bottom')
        },
          this.renderUserName(),
          this.renderUserPhoto()
        )
      )
    )
  }

  renderImage() {
    const { collection } = this.props
    let coverImageUrl
    if (collection['cover_image_urls']) {
      coverImageUrl = collection['cover_image_urls'].large
    }
    return (
      tag('div', {
        className: bem.element('image-container')
      },
        tag(LazyImage, {
          className: bem.element('image'),
          sources: [coverImageUrl, ...collection.images]
        }),
        tag('div', {
          className: bem.element('image-overlay')
        })
      )
    )
  }

  renderPositionedCircleContent() {
    if (this.props.isSelf || !this.props.onFollowClick) {
      return null
    }
    return (
      tag(CirclePositioner, {
        className: bem.element('positioned')
      },
        this.renderSubscribeButton(),
        this.renderSubscribersCount()
      )
    )
  }

  renderSubscribeButton() {
    const { isFollowing } = this.props.collection
    return tag(CollectionSubscribeCircleButton, {
      className: bem.element('subscribe-button'),
      checked: isFollowing,
      onClick: this.onFollowClick
    })
  }

  renderSubscribersCount() {
    const { collection, hasSubscribersCount } = this.props
    const count = collection['follower_count']
    if (!hasSubscribersCount || !count) {
      return null
    }
    return tag('a', {
      className: bem.element('subscribers-count'),
      href: this.collectionSubscribersHref(),
      title: `This Collection has ${count} ${Subscribers(count)}`,
      onClick: this.onCountClick
    },
      `${formatCount(count)}`
    )
  }

  renderUserName() {
    const { collection: { username } } = this.props
    return (
      tag('a', {
        className: bem.element('user-href'),
        href: this.userHref(),
        onClick: this.onUsernameClick
      },
        `by `,
        tag('em', {
          className: bem.element('user-name')
        },
          `@${username}`
        )
      )
    )
  }

  renderUserPhoto() {
    return tag(UserAvatar, {
      className: bem.element('user-photo'),
      href: true,
      user: this.props.collection,
      onClick: this.onUsernameClick
    })
  }

  render() {
    return (
      // Lockup wrapper
      tag('div', {
        className: classNames(
          this.props.className,
          bem.block({
            interactive: !!this.props.onCardClick
          })
        ),
        dataset: {
          mixId: this.props.collection.id
        },
        onClick: this.onCardClick
      },
        tag('div', {
          className: bem.element('inner')
        },
          this.renderImage(),
          this.renderDetails(),
          this.renderPositionedCircleContent()
        )
      )
    )
  }
}

CollectionCard.propTypes = {
  // Flags
  hasSubscribersCount: PropTypes.bool,
  isHrefsEnabled: PropTypes.bool,
  isSelf: PropTypes.bool,
  isUserVisible: PropTypes.bool,
  // Values
  className: PropTypes.string,
  collection: PropTypes.shape(CollectionModelRecord.propTypes).isRequired,
  pageContext: PropTypes.shape(PageContextRecord.propTypes),
  // Callbacks
  onCardClick: PropTypes.func,
  onFollowClick: PropTypes.func
}

// Wrapped event handler callbacks
CollectionCard.eventHandlerKeys = [
  'onCardClick',
  'onCountClick',
  'onFollowClick',
  'onTitleClick',
  'onUsernameClick'
]
CollectionCard.eventHandlerKeys.forEach(key => {
  CollectionCard.propTypes[key] = PropTypes.func
})

CollectionCard.defaultProps = {
  // Flags
  hasSubscribersCount: true,
  isHrefsEnabled: true,
  isUserVisible: true
}

module.exports = CollectionCard
